import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notificationKeys } from '@/services/queryKeys';
import { notificationService } from '@/services/notification.service';
import { mockNotifications, type TenantNotification } from '@/mocks/notifications';
import { isArrayResponse, hasKey, withMockFallback } from '@/utils/queryMock';

const memStore: TenantNotification[] = [...mockNotifications];

async function mockList(): Promise<TenantNotification[]> {
  await new Promise((r) => setTimeout(r, 80));
  return [...memStore];
}

async function mockUnreadCount(): Promise<{ count: number }> {
  return { count: memStore.filter((n) => !n.read).length };
}

async function mockMarkRead(id: string): Promise<{ ok: true }> {
  const item = memStore.find((n) => n._id === id);
  if (item) item.read = true;
  return { ok: true };
}

async function mockMarkAllRead(): Promise<{ ok: true }> {
  memStore.forEach((n) => (n.read = true));
  return { ok: true };
}

export function useNotificationList() {
  return useQuery({
    queryKey: notificationKeys.lists(),
    queryFn: () =>
      withMockFallback(() => notificationService.list(), mockList, {
        label: 'notifications.list',
        validate: isArrayResponse,
      }),
    refetchInterval: 60_000,
    retry: 0,
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: () =>
      withMockFallback(() => notificationService.unreadCount(), mockUnreadCount, {
        label: 'notifications.unreadCount',
        validate: hasKey('count'),
      }),
    refetchInterval: 60_000,
    retry: 0,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      withMockFallback(
        () => notificationService.markRead(id),
        () => mockMarkRead(id),
        { label: 'notifications.markRead' },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      withMockFallback(() => notificationService.markAllRead(), mockMarkAllRead, {
        label: 'notifications.markAllRead',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}
